import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { DivisionMileStone } from "../../../../../../../../../api/divisionMileStones/types";
import { CombinedMilestoneData, CombinedMilestoneTask } from "../types";
import {
     isEqual,
     getTaskCheckboxState,
     createAutomatedTaskNameSet,
} from "../utils";

export const useMilestoneState = (combinedMilestoneData: CombinedMilestoneData, getTasksForMilestone: (milestoneName: string) => CombinedMilestoneTask[]) => {
     // ========================================
     // STATE MANAGEMENT
     // ========================================

     // Stable references for expensive computations
     const prevDataRef = useRef<CombinedMilestoneData | null>(null);
     const prevTasksFnRef = useRef<typeof getTasksForMilestone | null>(null);

     // Local state management
     const [selectedMilestones, setSelectedMilestones] = useState<{ [key: string]: boolean; }>({});
     const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
     const [selectedMilestoneId, setSelectedMilestoneId] = useState<string | null>(null);
     const [expandedAll, setExpandedAll] = useState<boolean>(false);
     const [expandedAccordions, setExpandedAccordions] = useState<{ [key: string]: boolean; }>({});
     const [localTaskStates, setLocalTaskStates] = useState<{ [key: string]: boolean }>({});

     // ========================================
     // MEMOIZED COMPUTATIONS
     // ========================================

     // Memoized automated tasks extraction
     const automatedTasks = useMemo(() => {
          if (!combinedMilestoneData || !Array.isArray(combinedMilestoneData)) {
               return [];
          }

          const automated: DivisionMileStone[] = [];
          for (const milestone of combinedMilestoneData) {
               for (const task of milestone.tasks) {
                    if (task.divisionTemplate.automated) {
                         automated.push(task.divisionTemplate);
                    }
               }
          }
          return automated;
     }, [combinedMilestoneData]);

     // Memoized state computation
     const { milestoneStates, taskStates, completedCount, totalMilestones, automatedTaskNames } = useMemo(() => {
          if (!combinedMilestoneData) {
               return {
                    milestoneStates: {},
                    taskStates: {},
                    completedCount: 0,
                    totalMilestones: 0,
                    automatedTaskNames: new Set<string>()
               };
          }

          const milestoneStates: { [key: string]: boolean } = {};
          const taskStates: { [key: string]: boolean } = {};
          let completedCount = 0;
          let totalMilestones = combinedMilestoneData.length;

          // Extract automated tasks once
          const automatedTaskNames = createAutomatedTaskNameSet(automatedTasks);

          // Single pass through all milestones
          for (const milestone of combinedMilestoneData) {
               const tasks = getTasksForMilestone(milestone.milestoneName);
               const milestoneId = milestone.milestoneName;

               // Calculate milestone completion state
               // Check if all tasks in this milestone have division status = false (disabled at division level)
               const allTasksDisabledAtDivision = tasks.length > 0 && tasks.every(task => task.divisionTemplate.status === false);
               
               // Filter out tasks that are disabled at division level (status === false)
               const activeTasks = tasks.filter(task => task.divisionTemplate.status !== false);

               // Check if milestone is marked as "not applicable" by division admin
               const isNotApplicable = activeTasks.length > 0 && activeTasks.every((task) =>
                    task.milestoneTaskData?.status === false &&
                    task.milestoneTaskData?.completionDate !== null
               );

               // Set milestone state for UI
               if (activeTasks.length > 0) {
                    milestoneStates[milestoneId] = activeTasks.every((task) => task.milestoneTaskData?.status === true);
               }

               // Count completed milestones - include:
               // 1. Milestones where all tasks are disabled at division level (division status = false)
               // 2. Milestones where all active tasks are completed
               // 3. Milestones marked as "not applicable"
               const allActiveTasksCompleted = activeTasks.length > 0 && activeTasks.every((task) => {
                    // Check if status is true or false (not null)
                    return task.milestoneTaskData?.status === true || task.milestoneTaskData?.status === false;
               });

               // Count milestone as completed if:
               // - All tasks are disabled at division level, OR
               // - All active tasks are completed, OR
               // - It's marked as not applicable
               if (allTasksDisabledAtDivision || allActiveTasksCompleted || isNotApplicable) {
                    completedCount++;
               }

               // Calculate individual task states
               for (const task of milestone.tasks) {
                    taskStates[task.divisionTemplate.taskName] = getTaskCheckboxState(task);
               }
          }

          return {
               milestoneStates,
               taskStates,
               completedCount,
               totalMilestones,
               automatedTaskNames
          };
     }, [combinedMilestoneData, getTasksForMilestone, automatedTasks]);

     // Check if we can reuse previous computation
     const shouldRecompute = useMemo(() => {
          return !isEqual(prevDataRef.current, combinedMilestoneData) ||
               prevTasksFnRef.current !== getTasksForMilestone;
     }, [combinedMilestoneData, getTasksForMilestone]);

     // ========================================
     // EFFECTS
     // ========================================

     // Update refs when data changes
     useEffect(() => {
          if (shouldRecompute) {
               prevDataRef.current = combinedMilestoneData;
               prevTasksFnRef.current = getTasksForMilestone;
          }
     }, [shouldRecompute, combinedMilestoneData, getTasksForMilestone]);

     // Update local state when computed values change
     useEffect(() => {
          setSelectedMilestones(milestoneStates);
          setLocalTaskStates(taskStates);
     }, [milestoneStates, taskStates]);

     // ========================================
     // CALLBACK FUNCTIONS
     // ========================================

     // Task completion check functions
     const areTasksCompleted = useCallback(
          (milestoneName: string): boolean => {
               const tasks = getTasksForMilestone(milestoneName);
               // Only consider active tasks (not disabled at division level)
               const activeTasks = tasks.filter(task => task.divisionTemplate.status !== false);
               return activeTasks.every((task) => task.milestoneTaskData?.status !== null);
          },
          [getTasksForMilestone]
     );

     const findAutomatedTasks = useCallback(
          (tasks: CombinedMilestoneTask[]) => {
               return tasks.filter(task => automatedTaskNames.has(task.divisionTemplate.taskName));
          },
          [automatedTaskNames]
     );

     const isNotApplicableMilestone = useCallback(
          (milestoneName: string): boolean => {
               const tasks = getTasksForMilestone(milestoneName);
               // Filter out tasks that are disabled at division level
               const activeTasks = tasks.filter(task => task.divisionTemplate.status !== false);
               return activeTasks.every((task) =>
                    task.milestoneTaskData?.status === false &&
                    task.milestoneTaskData?.completionDate !== null
               );
          },
          [getTasksForMilestone]
     );

     // UI interaction functions
     const toggleExpandAll = useCallback(() => {
          setExpandedAll(prev => {
               const newState = !prev;

               if (combinedMilestoneData) {
                    const newAccordionStates: { [key: string]: boolean } = {};
                    for (const milestone of combinedMilestoneData) {
                         const milestoneId = milestone.milestoneName;
                         newAccordionStates[milestoneId] = newState;
                    }
                    setExpandedAccordions(newAccordionStates);
               }

               return newState;
          });
     }, [combinedMilestoneData]);

     const toggleAccordion = useCallback((id: string) => {
          setExpandedAccordions(prev => ({ ...prev, [id]: !prev[id] }));
     }, []);

     const handleMenuClick = useCallback(
          (event: React.MouseEvent<HTMLElement>, milestoneId: string) => {
               event.stopPropagation();
               setMenuAnchorEl(event.currentTarget);
               setSelectedMilestoneId(milestoneId);
          },
          []
     );

     const handleMenuClose = useCallback(() => {
          setMenuAnchorEl(null);
          setSelectedMilestoneId(null);
     }, []);

     // ========================================
     // RETURN OBJECT
     // ========================================

     // Memoized return object with stable references
     return useMemo(() => ({
          selectedMilestones,
          setSelectedMilestones,
          menuAnchorEl,
          setMenuAnchorEl,
          selectedMilestoneId,
          setSelectedMilestoneId,
          expandedAll,
          expandedAccordions,
          taskStates: localTaskStates,
          setTaskStates: setLocalTaskStates,
          automatedTasks,
          areTasksCompleted,
          findAutomatedTasks,
          isNotApplicableMilestone,
          getCompletedCounts: completedCount,
          getTotalMilestones: totalMilestones,
          toggleExpandAll,
          toggleAccordion,
          handleMenuClick,
          handleMenuClose,
     }), [
          selectedMilestones,
          menuAnchorEl,
          selectedMilestoneId,
          expandedAll,
          expandedAccordions,
          localTaskStates,
          automatedTasks,
          areTasksCompleted,
          findAutomatedTasks,
          isNotApplicableMilestone,
          completedCount,
          totalMilestones,
          toggleExpandAll,
          toggleAccordion,
          handleMenuClick,
          handleMenuClose,
     ]);
}; 